import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, NotebookPen, ThumbsUp, ThumbsDown, BookOpen } from "@/lib/icons";
import type { Reaction } from "@/data/reactions";

const PREDICT_FIRST_KEY = "elementpad:lab:predict-first";

interface EquationPredictionProps {
  reaction: Reaction;
  /**
   * True once the beaker has finished its reaction animation and the
   * full ReactionResult is showing. Used to invite a "did this match?"
   * self-check after the reveal.
   */
  showResult: boolean;
}

/**
 * Shows the predicted balanced equation pulled from the reaction database,
 * with an optional "Predict first" mode that hides the equation behind a
 * reveal button so students can guess before peeking.
 */
export default function EquationPrediction({ reaction, showResult }: EquationPredictionProps) {
  const [predictFirst, setPredictFirst] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(PREDICT_FIRST_KEY) === "1";
  });
  const [revealed, setRevealed] = useState(!predictFirst);
  const [feedback, setFeedback] = useState<"yes" | "no" | null>(null);

  // Reset reveal state whenever a different reaction enters the beaker.
  useEffect(() => {
    setRevealed(!predictFirst);
    setFeedback(null);
  }, [reaction.id, predictFirst]);

  const togglePredictFirst = () => {
    const next = !predictFirst;
    setPredictFirst(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(PREDICT_FIRST_KEY, next ? "1" : "0");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25 }}
      className="w-full max-w-md rounded-xl border border-primary/30 bg-primary/5 p-4"
      role="region"
      aria-label="Predicted equation"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
          <NotebookPen size={12} aria-hidden="true" />
          Predicted equation
        </div>
        <button
          type="button"
          onClick={togglePredictFirst}
          aria-pressed={predictFirst}
          aria-label={predictFirst ? "Disable predict-first mode" : "Enable predict-first mode"}
          className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border transition-colors ${
            predictFirst
              ? "bg-primary/15 text-primary border-primary/40"
              : "bg-background text-muted-foreground border-border hover:text-foreground"
          }`}
        >
          {predictFirst ? <EyeOff size={10} aria-hidden="true" /> : <Eye size={10} aria-hidden="true" />}
          Predict first
        </button>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {revealed ? (
          <motion.div
            key="revealed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="font-mono text-sm text-foreground bg-background/60 rounded-md px-3 py-2 border border-border">
              {reaction.equation}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1.5 flex items-center gap-1">
              <BookOpen size={10} aria-hidden="true" />
              Balanced • {reaction.typeLabel}
            </div>

            {/* Self-check prompt - only after the visible reaction completes */}
            {showResult && predictFirst && feedback === null && (
              <div className="mt-3 pt-3 border-t border-border/60">
                <div className="text-[11px] text-muted-foreground mb-1.5">Did this match your prediction?</div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => setFeedback("yes")}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 transition-colors"
                  >
                    <ThumbsUp size={11} aria-hidden="true" />
                    Yes, I had it
                  </button>
                  <button
                    type="button"
                    onClick={() => setFeedback("no")}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <ThumbsDown size={11} aria-hidden="true" />
                    Not quite
                  </button>
                </div>
              </div>
            )}
            {feedback === "yes" && (
              <p className="text-[11px] text-emerald-500 mt-2">Nice work - your intuition is sharpening.</p>
            )}
            {feedback === "no" && (
              <p className="text-[11px] text-muted-foreground mt-2">
                Try the <a href="/equation-balancer" className="text-primary underline-offset-2 hover:underline">Equation Balancer</a> to practice the rules.
              </p>
            )}
          </motion.div>
        ) : (
          <motion.button
            key="hidden"
            type="button"
            onClick={() => setRevealed(true)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full font-mono text-sm bg-background/60 rounded-md px-3 py-2 border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors text-left"
            aria-label="Reveal the predicted balanced equation"
          >
            <span className="select-none tracking-widest">▮ ▮ ▮ → ▮ ▮ ▮</span>
            <span className="block text-[10px] mt-0.5 text-muted-foreground/80">Predict the equation, then click to reveal</span>
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
}